import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const LanguageContext = createContext();

const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('crystal-language');
    return saved || 'en';
  });

  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/languages`);
        setLanguages(response.data.data);
      } catch (error) {
        console.error('Failed to load languages:', error);
        setLanguages([
          { code: 'en', name: 'English', native_name: 'English', flag_emoji: '🇬🇧' },
          { code: 'tr', name: 'Turkish', native_name: 'Türkçe', flag_emoji: '🇹🇷' },
          { code: 'ru', name: 'Russian', native_name: 'Русский', flag_emoji: '🇷🇺' },
          { code: 'no', name: 'Norwegian', native_name: 'Norsk', flag_emoji: '🇳🇴' }
        ]);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    const loadTranslations = async () => {
      setLoading(true);
      try {
        const module = await import(`../i18n/${currentLanguage}.json`);
        setTranslations(module.default);
      } catch (error) {
        console.error('Failed to load translations:', error);
        const enModule = await import('../i18n/en.json');
        setTranslations(enModule.default);
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
    localStorage.setItem('crystal-language', currentLanguage);
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return fallback;
    }

    return value;
  };

  const changeLanguage = (code) => {
    setCurrentLanguage(code);
  };

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      languages,
      translations,
      loading,
      t,
      changeLanguage
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
